import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  /** Value in ISO format `yyyy-mm-dd` (same as a native <input type="date">). */
  value: string;
  onChange: (value: string) => void;
  /** Inclusive lower bound, `yyyy-mm-dd`. */
  min?: string;
  /** Inclusive upper bound, `yyyy-mm-dd`. */
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Classes applied to the <input> element. */
  className?: string;
  /** Classes applied to the wrapper (use for flex sizing, e.g. `flex-1 min-w-0`). */
  wrapperClassName?: string;
  id?: string;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Local-time helpers (avoid UTC shift that `new Date('yyyy-mm-dd')` introduces)
const toISO = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseISO = (s?: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatDisplay = (s?: string): string => {
  const d = parseISO(s);
  if (!d) return '';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const CAL_WIDTH = 288; // w-72
const CAL_HEIGHT = 340;

export default function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'dd-mm-yyyy',
  disabled = false,
  className = '',
  wrapperClassName = '',
  id,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseISO(value), [value]);
  const [viewMonth, setViewMonth] = useState<Date>(() => selected ?? new Date());

  const minDate = useMemo(() => parseISO(min), [min]);
  const maxDate = useMemo(() => parseISO(max), [max]);

  // Keep the displayed month in sync when the value changes while closed.
  // Adjusted during render (React's documented "derive state from props" pattern)
  // rather than in an effect, which would cost an extra render pass.
  const selectedMonthKey = selected ? `${selected.getFullYear()}-${selected.getMonth()}` : null;
  const [syncedMonthKey, setSyncedMonthKey] = useState<string | null>(selectedMonthKey);
  if (selectedMonthKey && selectedMonthKey !== syncedMonthKey) {
    setSyncedMonthKey(selectedMonthKey);
    setViewMonth(new Date(selected!.getFullYear(), selected!.getMonth(), 1));
  }

  // Position the popup within the viewport (open above / shift left as needed)
  const reposition = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    let left = rect.left;
    if (left + CAL_WIDTH > window.innerWidth - margin) {
      left = window.innerWidth - CAL_WIDTH - margin;
    }
    if (left < margin) left = margin;

    let top = rect.bottom + 4;
    if (top + CAL_HEIGHT > window.innerHeight - margin) {
      const above = rect.top - CAL_HEIGHT - 4;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - CAL_HEIGHT - margin);
    }
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const handler = () => reposition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [open, reposition]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        popupRef.current?.contains(e.target as Node) ||
        anchorRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    // Escape is consumed in the CAPTURE phase so an enclosing modal's own
    // Escape handler doesn't also fire and close the whole dialog.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const isDisabled = (d: Date): boolean => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const isSameDay = (a: Date | null, b: Date | null): boolean =>
    !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const today = new Date();

  // Build the 6x7 grid of days for the displayed month
  const days = useMemo(() => {
    const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [viewMonth]);

  const selectDay = (d: Date) => {
    if (isDisabled(d)) return;
    onChange(toISO(d));
    setOpen(false);
  };

  const changeMonth = (delta: number) =>
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  return (
    <>
      <div ref={anchorRef} className={`relative ${wrapperClassName}`}>
        <input
          id={id}
          type="text"
          readOnly
          disabled={disabled}
          value={formatDisplay(value)}
          placeholder={placeholder}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={`w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg cursor-pointer focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          className="absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400 hover:text-gray-600"
        >
          <Calendar className="h-4 w-4" />
        </button>
      </div>

      {open && createPortal(
        <div
          ref={popupRef}
          className="bg-white border border-gray-200 rounded-xl shadow-2xl p-3 w-72"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
        >
          {/* Month / year navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">
              {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
            </span>
            <button
              type="button"
              onClick={() => changeMonth(1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-xs font-medium text-gray-400 py-1">
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((d, i) => {
              const inMonth = d.getMonth() === viewMonth.getMonth();
              const disabledDay = isDisabled(d);
              const isSelected = isSameDay(d, selected);
              const isToday = isSameDay(d, today);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabledDay}
                  onClick={() => selectDay(d)}
                  className={`h-8 w-8 mx-auto flex items-center justify-center text-sm rounded-lg transition-colors
                    ${isSelected ? 'bg-indigo-600 text-white font-semibold' : ''}
                    ${!isSelected && isToday ? 'border border-indigo-400 text-indigo-600 font-semibold' : ''}
                    ${!isSelected && !isToday && inMonth ? 'text-gray-700' : ''}
                    ${!inMonth ? 'text-gray-300' : ''}
                    ${disabledDay ? 'text-gray-200 cursor-not-allowed' : 'hover:bg-gray-100'}
                    ${isSelected ? 'hover:bg-indigo-600' : ''}`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                if (!isDisabled(today)) { onChange(toISO(today)); setOpen(false); }
              }}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              Today
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
